/** @jsxImportSource @emotion/react */

import React from 'react';
import styled from 'styled-components';
import * as color from '@constant/color';
import * as size from '@constant/size';
import { IS_IOS, IS_IYAGIGO_MOBILE_VIEW, IS_REACT_NATIVE_APP } from '@config/environment';
import { ETheme } from '@datatype/type';
import { dismissNoticeByDay } from '@hook/iyagigo/useNotification';
import { useTypedDispatch } from '@hook/common/useStore';
import { actions as modalActions } from '@store/slice/modal-slice';
import TextBlock from '@component/common/block/text-block';
import TextButton from '@design-system/text-button';
import ImageBlock from '@component/common/block/image-block';
import Gap from '@component/common/gap';
import appStoreBadge from '@asset/image/app-store-badge.svg';
import googlePlayBadge from '@asset/image/google-play-badge.png';

const IYAGIGO_IOS_STORE_URL = 'https://apps.apple.com/app/id6759956246';
const IYAGIGO_ANDROID_STORE_URL = 'https://play.google.com/store/apps/details?id=com.iyagigo';

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: ${size.MEDIUM_MODAL_WIDTH};
`;

const StoreButtonContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  gap: ${size.MARGIN_1};
`;

const DismissContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

const SpeakingAppNoticeModal = () => {
  const dispatch = useTypedDispatch();

  const openStore = (storeUrl: string) => {
    dismissNoticeByDay();
    dispatch(modalActions.hideModal());
    window.open(storeUrl, '_blank');
  };

  const handleApplicationClick = () => {
    openStore(IS_IOS ? IYAGIGO_IOS_STORE_URL : IYAGIGO_ANDROID_STORE_URL);
  };

  const handleDismissClick = () => {
    dismissNoticeByDay();
    dispatch(modalActions.hideModal());
  };

  return (
    <Wrapper>
      <TextBlock
        text='AI 말하기가 출시됐어요'
        design={{ text: { size: 'large', color: color.BLACK_2, bold: true } }}
      />
      <Gap height='5px' />
      <TextBlock
        text='AI 말하기를 사용하려면<br/>최신 버전으로 업데이트해 주세요'
        design={{
          text: { size: 'medium', color: color.GRAY_1, bold: false },
          box: { alignX: 'center', width: '100%' },
        }}
      />
      <Gap height={size.MARGIN_2} />
      {IS_REACT_NATIVE_APP || IS_IYAGIGO_MOBILE_VIEW ? (
        <TextButton
          text='앱 업데이트'
          theme={ETheme.BROWN}
          size='medium'
          shape='pill'
          width='100%'
          handleClick={handleApplicationClick}
        />
      ) : (
        <StoreButtonContainer>
          <ImageBlock
            src={appStoreBadge}
            alt='App Store'
            design={{ image: { height: '48px' }, box: { event: { cursor: 'pointer' } } }}
            click={{ handler: () => openStore(IYAGIGO_IOS_STORE_URL) }}
          />
          <ImageBlock
            src={googlePlayBadge}
            alt='Google Play'
            design={{ image: { height: '48px' }, box: { event: { cursor: 'pointer' } } }}
            click={{ handler: () => openStore(IYAGIGO_ANDROID_STORE_URL) }}
          />
        </StoreButtonContainer>
      )}
      <Gap height={size.MARGIN_1} />
      <DismissContainer>
        <TextBlock
          text='일주일간 보지 않기'
          design={{
            text: { size: 'small', color: color.GRAY_1, bold: false, underline: true },
            box: { event: { cursor: 'pointer' } },
          }}
          click={{ handler: handleDismissClick }}
        />
      </DismissContainer>
    </Wrapper>
  );
};

export default SpeakingAppNoticeModal;
